import { useState, useRef } from 'react';
import Layout from '../components/shared/Layout';
import { updateProfile, uploadLogo, uploadHeadshot } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './SettingsPage.css';

const COLORS = ['#1B3F6B','#2a5298','#0f766e','#7c3aed','#b91c1c','#b45309','#15803d','#0e7490'];

export default function SettingsPage() {
  const { agent, refreshAgent } = useAuth();
  const [form, setForm] = useState({
    full_name:    agent?.full_name    || '',
    phone:        agent?.phone        || '',
    brokerage:    agent?.brokerage    || '',
    accent_color: agent?.accent_color || '#1B3F6B',
  });
  const [saving, setSaving]         = useState(false);
  const [success, setSuccess]       = useState('');
  const [error, setError]           = useState('');
  const [uploadingLogo, setUploadingLogo]       = useState(false);
  const [uploadingHead, setUploadingHead]       = useState(false);
  const logoRef = useRef();
  const headRef = useRef();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      await updateProfile(form);
      await refreshAgent();
      setSuccess('Profile saved successfully.');
    } catch {
      setError('Could not save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogo = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploadingLogo(true);
    try { await uploadLogo(file); await refreshAgent(); setSuccess('Logo updated.'); }
    catch { setError('Could not upload logo.'); }
    finally { setUploadingLogo(false); }
  };

  const handleHead = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploadingHead(true);
    try { await uploadHeadshot(file); await refreshAgent(); setSuccess('Headshot updated.'); }
    catch { setError('Could not upload headshot.'); }
    finally { setUploadingHead(false); }
  };

  return (
    <Layout>
      <div style={{maxWidth: 680}}>
        <h1 style={{fontSize:24, fontWeight:800, marginBottom:4}}>Account Settings</h1>
        <p style={{color:'var(--gray-400)', marginBottom:28, fontSize:14}}>
          Your info and branding appears on every report you generate.
        </p>

        {success && <div className="alert alert-success">{success}</div>}
        {error   && <div className="alert alert-error">{error}</div>}

        {/* Profile form */}
        <div className="card" style={{marginBottom:20}}>
          <h2 className="card-title" style={{marginBottom:20}}>Profile Information</h2>
          <form onSubmit={handleSave}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className="form-input" required value={form.full_name}
                  onChange={e => set('full_name', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Phone Number</label>
                <input className="form-input" value={form.phone}
                  onChange={e => set('phone', e.target.value)} placeholder="555-867-5309" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Brokerage Name</label>
              <input className="form-input" value={form.brokerage}
                onChange={e => set('brokerage', e.target.value)} placeholder="EXP Realty" />
            </div>

            {/* Accent color */}
            <div className="form-group">
              <label className="form-label">Report Accent Color</label>
              <p className="form-hint" style={{marginBottom:10}}>This color is used for headings and highlights throughout your report.</p>
              <div className="color-swatches">
                {COLORS.map(c => (
                  <button key={c} type="button"
                    className={`color-swatch ${form.accent_color === c ? 'selected' : ''}`}
                    style={{background: c}}
                    onClick={() => set('accent_color', c)}
                  />
                ))}
                <div className="color-custom">
                  <input type="color" value={form.accent_color}
                    onChange={e => set('accent_color', e.target.value)}
                    title="Custom color" />
                </div>
              </div>
              <div className="color-preview" style={{borderColor: form.accent_color, color: form.accent_color}}>
                Preview: Your PAR Report heading will appear in this color
              </div>
            </div>

            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? <><div className="spinner"/>Saving…</> : 'Save Profile'}
            </button>
          </form>
        </div>

        {/* Branding uploads */}
        <div className="card">
          <h2 className="card-title" style={{marginBottom:4}}>Branding</h2>
          <p className="card-subtitle" style={{marginBottom:20}}>Your logo appears on the cover page and footer of every report.</p>

          <div className="upload-row">
            <div className="upload-item">
              <label className="form-label">Brokerage Logo</label>
              <div className="upload-box" onClick={() => logoRef.current.click()}>
                {agent?.logo_url
                  ? <img src={agent.logo_url} alt="logo" className="upload-preview"/>
                  : <div className="upload-placeholder">
                      <span>🏢</span><p>Click to upload</p>
                    </div>
                }
              </div>
              <input ref={logoRef} type="file" accept="image/*" style={{display:'none'}} onChange={handleLogo}/>
              <button className="btn btn-ghost btn-sm" style={{marginTop:8}} onClick={() => logoRef.current.click()} disabled={uploadingLogo}>
                {uploadingLogo ? 'Uploading…' : agent?.logo_url ? 'Replace Logo' : 'Upload Logo'}
              </button>
            </div>

            <div className="upload-item">
              <label className="form-label">Agent Headshot <span style={{fontWeight:400, textTransform:'none', letterSpacing:0}}>(optional)</span></label>
              <div className="upload-box upload-box-round" onClick={() => headRef.current.click()}>
                {agent?.headshot_url
                  ? <img src={agent.headshot_url} alt="headshot" className="upload-preview upload-preview-round"/>
                  : <div className="upload-placeholder">
                      <span>👤</span><p>Click to upload</p>
                    </div>
                }
              </div>
              <input ref={headRef} type="file" accept="image/*" style={{display:'none'}} onChange={handleHead}/>
              <button className="btn btn-ghost btn-sm" style={{marginTop:8}} onClick={() => headRef.current.click()} disabled={uploadingHead}>
                {uploadingHead ? 'Uploading…' : agent?.headshot_url ? 'Replace Headshot' : 'Upload Headshot'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
